import { useMemo, useRef, useState } from 'react';
import { euros } from '../utils/format';

export interface PointBilan {
  annee: number;
  totalVentes: number;
  achats: number;
  solde: number;
}

/**
 * Les trois courbes du bilan : ventes, achats, solde, année par année.
 *
 * Palette validée pour le daltonisme (vert/orange/bleu — le rouge contre le
 * vert ne passe pas le contrôle deutéranope) ; l'identité ne repose pas sur
 * la seule couleur : légende, étiquettes directes en bout de ligne, et le
 * survol donne les trois valeurs de l'année pointée.
 */
const SERIES = [
  { cle: 'totalVentes', libelle: 'Ventes', couleur: '#16a34a' },
  { cle: 'achats', libelle: 'Achats', couleur: '#ea580c' },
  { cle: 'solde', libelle: 'Solde', couleur: '#2563eb' },
] as const;

const LARGEUR = 640;
const HAUTEUR = 260;
const MARGE = { haut: 16, droite: 76, bas: 28, gauche: 56 };

export default function CourbesBilan({ serie }: { serie: PointBilan[] }) {
  const [surligne, setSurligne] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const { xDe, yDe, graduations } = useMemo(() => {
    const valeurs = serie.flatMap((p) => [p.totalVentes, p.achats, p.solde]);
    // Le zéro reste visible : un solde peut être négatif, et une échelle qui
    // le cacherait ferait passer une perte pour un creux.
    const min = Math.min(0, ...valeurs);
    const max = Math.max(1, ...valeurs);
    const amplitude = max - min || 1;

    const largeurTrace = LARGEUR - MARGE.gauche - MARGE.droite;
    const hauteurTrace = HAUTEUR - MARGE.haut - MARGE.bas;

    const xDe = (i: number) =>
      MARGE.gauche + (serie.length === 1 ? largeurTrace / 2 : (i / (serie.length - 1)) * largeurTrace);
    const yDe = (v: number) => MARGE.haut + hauteurTrace - ((v - min) / amplitude) * hauteurTrace;

    // Quatre graduations arrondies au « joli » palier.
    const brut = amplitude / 4;
    const puissance = 10 ** Math.floor(Math.log10(brut));
    const pas = Math.ceil(brut / puissance) * puissance;
    const graduations: number[] = [];
    for (let v = Math.ceil(min / pas) * pas; v <= max; v += pas) graduations.push(v);

    return { xDe, yDe, graduations };
  }, [serie]);

  if (serie.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
        Aucune année à tracer pour le moment.
      </p>
    );
  }

  const chemin = (cle: (typeof SERIES)[number]['cle']) =>
    serie.map((p, i) => `${i === 0 ? 'M' : 'L'}${xDe(i).toFixed(1)},${yDe(p[cle]).toFixed(1)}`).join(' ');

  const pointe = surligne === null ? null : serie[surligne];

  return (
    <div className="space-y-2">
      {/* Légende : l'identité des séries, jamais par la couleur seule. */}
      <div className="flex flex-wrap gap-4 text-sm">
        {SERIES.map((s) => (
          <span key={s.cle} className="inline-flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
            <span className="w-3 h-0.5 rounded" style={{ backgroundColor: s.couleur }} aria-hidden="true" />
            {s.libelle}
          </span>
        ))}
      </div>

      <div className="relative overflow-x-auto">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${LARGEUR} ${HAUTEUR}`}
          className="w-full min-w-[420px]"
          role="img"
          aria-label="Ventes, achats et solde par année"
          onMouseLeave={() => setSurligne(null)}
          onMouseMove={(e) => {
            const rect = svgRef.current?.getBoundingClientRect();
            if (!rect) return;
            const x = ((e.clientX - rect.left) / rect.width) * LARGEUR;
            let plusProche = 0;
            for (let i = 1; i < serie.length; ++i) {
              if (Math.abs(xDe(i) - x) < Math.abs(xDe(plusProche) - x)) plusProche = i;
            }
            setSurligne(plusProche);
          }}
        >
          {/* Grille récessive : des repères, pas un quadrillage. */}
          {graduations.map((v) => (
            <g key={v}>
              <line
                x1={MARGE.gauche}
                x2={LARGEUR - MARGE.droite}
                y1={yDe(v)}
                y2={yDe(v)}
                stroke="currentColor"
                className="text-gray-200 dark:text-gray-700"
                strokeWidth={v === 0 ? 1.5 : 1}
              />
              <text
                x={MARGE.gauche - 8}
                y={yDe(v) + 4}
                textAnchor="end"
                className="fill-gray-500 dark:fill-gray-400"
                fontSize={11}
              >
                {v >= 1000 || v <= -1000 ? `${v / 1000} k€` : `${v} €`}
              </text>
            </g>
          ))}

          {serie.map((p, i) => (
            <text
              key={p.annee}
              x={xDe(i)}
              y={HAUTEUR - 8}
              textAnchor="middle"
              className="fill-gray-500 dark:fill-gray-400"
              fontSize={12}
            >
              {p.annee}
            </text>
          ))}

          {/* Repère de l'année survolée. */}
          {surligne !== null && (
            <line
              x1={xDe(surligne)}
              x2={xDe(surligne)}
              y1={MARGE.haut}
              y2={HAUTEUR - MARGE.bas}
              stroke="currentColor"
              className="text-gray-300 dark:text-gray-600"
              strokeDasharray="3 3"
            />
          )}

          {SERIES.map((s) => (
            <g key={s.cle}>
              <path d={chemin(s.cle)} fill="none" stroke={s.couleur} strokeWidth={2} strokeLinejoin="round" />
              {serie.map((p, i) => (
                <circle
                  key={p.annee}
                  cx={xDe(i)}
                  cy={yDe(p[s.cle])}
                  r={surligne === i ? 5 : 4}
                  fill={s.couleur}
                  stroke="white"
                  strokeWidth={2}
                  className="dark:stroke-gray-900"
                />
              ))}
              {/* Étiquette directe en bout de ligne : la série se nomme sans
                  aller-retour vers la légende. */}
              <text
                x={xDe(serie.length - 1) + 10}
                y={yDe(serie[serie.length - 1][s.cle]) + 4}
                fontSize={11}
                fontWeight={600}
                className="fill-gray-700 dark:fill-gray-300"
              >
                {s.libelle}
              </text>
            </g>
          ))}
        </svg>

        {/* Infobulle : les trois valeurs de l'année pointée. */}
        {pointe && surligne !== null && (
          <div
            className="absolute top-2 pointer-events-none rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg px-3 py-2 text-xs space-y-0.5"
            style={{
              left: `${(xDe(surligne) / LARGEUR) * 100}%`,
              transform: surligne > serie.length / 2 ? 'translateX(-105%)' : 'translateX(8px)',
            }}
          >
            <p className="font-semibold text-gray-900 dark:text-gray-100">{pointe.annee}</p>
            {SERIES.map((s) => (
              <p key={s.cle} className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300 tabular-nums">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.couleur }} aria-hidden="true" />
                {s.libelle} : {euros.format(pointe[s.cle])}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
